import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { setSelectedAssignedProjectId } from "@store/slices/translatorAdminSlice";
import { restrictSpecialCharacters, ROLE_ROUTES } from "@utils/constants";
import {
  fetchAllInProgressProjects,
  fetchAllTranslators,
} from "@store/thunks/translationThunk";

const ProjectDetailTableCommon = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { assignedProjects, selectedAssignedProjectId } =
    useSelector((store) => store.translatorAdmin);

  const { role } = useSelector((store) => store.auth);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [allProjects, setAllProjects] = useState([]);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setInitialLoading(true);
        if (role === "TRANSLATOR ADMIN") {
          const res = await dispatch(fetchAllInProgressProjects(searchQuery, pageIndex, pageSize));
          // await dispatch(fetchAllTranslators());
          if (Array.isArray(res)) {
            setAllProjects(res)
            if (res.length < pageSize) {
              setHasNextPage(false);
            } else {
              setHasNextPage(true);
            }
          } else {
            setAllProjects([]);
            setHasNextPage(false);
          }
          setInitialLoading(false);
        }
        else {
          const res = await dispatch(fetchAllInProgressProjects(searchQuery, pageIndex, pageSize));
          if (Array.isArray(res)) {
            setAllProjects(res)
            if (res.length < pageSize) {
              setHasNextPage(false);
            } else {
              setHasNextPage(true);
            }
          } else {
            setAllProjects([]);
            setHasNextPage(false);
          }
          setInitialLoading(false);
        }
        setInitialLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setError("Failed to fetch projects: " + error.message);
        setInitialLoading(false);
      }
    };

    fetchProjects();
  }, [dispatch, searchQuery, pageIndex, pageSize, role]);





  const handleProjectClick = ({ row }) => {
    const projectId = row.projectId || row.projectId;
    if (selectedAssignedProjectId === projectId) {
      dispatch(setSelectedAssignedProjectId(projectId));
    } else {
      dispatch(setSelectedAssignedProjectId(projectId));
    }
    navigate(`${destinationRoute}/folders`);
  };

  const projectColumns = [
    {
      header: "Sr. No.",
      cell: ({ row, table }) => {
        const rowIndex = row.index;

        return pageIndex * pageSize + rowIndex + 1; // Serial number starts from 1
      },
      cellClassName: "text-center",
    },
    {
      header: "Project Name",
      accessorKey: "projectName",
      cellClassName: "cursor-pointer hover:text-blue-600",
      onClick: handleProjectClick,
      cell: ({ row }) => (
        <div className="cursor-pointer text-[#ec7026] hover:text-[#d85d1a] focus:underline">
          {row.original.projectName}
        </div>
      ),
    },
    {
      header: "Description",
      accessorKey: "description",
      cellClassName: "cursor-pointer",
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cellClassName: "cursor-pointer hover:text-blue-600",
      cell: ({ row }) => {
        const deadline = row.original?.createdAt || row.createdAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
    },
    {
      header: "Deadline",
      accessorKey: "deadline",
      cellClassName: "cursor-pointer hover:text-blue-600",
      cell: ({ row }) => {
        const deadline = row.original?.deadline || row.deadline;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search projects..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {initialLoading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <p>{error}</p>
              <p className="mt-2">Please try again later or contact support.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              {allProjects.length === 0 ? (
                <div className="p-6">
                  <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p>No projects assigned yet.</p>
                    <p className="mt-2">
                      You have not been assigned any projects for validation. Please contact the
                      administrator if you believe this is an error.
                    </p>
                  </div>
                </div>
              ) : (
                <GenericTable
                  data={[...allProjects].filter(project => project.status !== "DELETED").sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                  )}
                  showPagination={true}
                  manualPagination={true}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  hasNextPage={hasNextPage}
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
                  columns={projectColumns}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailTableCommon;
